<template>
    <div class="facet-panel-container">
        <div class="facet-header">
            <span class="filter-applied-text">
                {{ activeFiltersCount > 0 ? `${activeFiltersCount} filter${activeFiltersCount>1?'s':''} applied`: 'No filters applied' }}
            </span>
            <button
                type="button"
                class="clear-all-link"
                :disabled="facetsLoading"
                :aria-label="`Clear all ${activeFiltersCount} filter${activeFiltersCount>1?'s':''}`"
                @click="resetFacetFilters()"
                @keydown.enter="resetFacetFilters()"
                @keydown.space.prevent="resetFacetFilters()"
            >
                Clear All
            </button>
        </div>
        <!-- UX-spec inline loader — sits between header and first facet section -->
        <Transition name="facet-overlay-fade">
            <div
                v-if="facetsLoading"
                class="facet-loading-indicator"
                role="status"
                aria-live="polite"
            >
                <div
                    class="facet-loading-icon"
                    aria-hidden="true"
                />
                <span class="facet-loading-text">Loading filters</span>
            </div>
        </Transition>
        <div
            class="facet-panel"
            v-if="hasActiveFacets"
            :class="{ 'facet-panel--loading': facetsLoading }"
        >
            <div
                v-for="section in facetSections"
                :key="section.title"
                :test-id="`facetSection-${section.id}`"
                class="facet-section"
            >
                <CollapsibleFacet
                    :facet-name="section.title"
                    :collapsed="section.collapsed"
                    :tooltip-text="
                        section.title === 'Users'
                            ? 'By default, all the Teams and Users that you have permission to view will be visible in the Table and Timeline view.'
                            : undefined
                    "
                >
                    <component
                        v-for="facet in visibleFacetsForSection(section)"
                        :is="facet.component"
                        :key="`${activeTable}-${resetKey}-${facet.id}`"
                        :is-loading="isLoading"
                        v-bind="getFacetBindings(facet)"
                        :facet-id="facet.id"
                        :facet-name="facet.name"
                        :active-table="activeTable"
                        :url-key="facet.urlKey"
                        @change="updateFacet(facet.id, activeTable, $event)"
                        style="margin-bottom: 16px;"
                    />
                </CollapsibleFacet>
            </div>
        </div>
    </div>
</template>
<script setup lang="ts">
import {
    ref, watch, computed, inject, onMounted, type Ref
} from 'vue';
import { getFacetConfig, getFacetSections } from './facetConfig';

import { FacetConfig, FacetItem } from '@/components/TeamAndSchedule/types/facetsTypes';
import type { InjectedPagePreferences } from '@agyletime/vue-3-common/src/typings/pagePreference';
import { useFacetVisibility } from '@/components/TeamAndSchedule/composables/useFacetVisibility';
import '@playvox/almendra/dist/components/icon';
import '@playvox/almendra/dist/components/checkbox';
import '@playvox/almendra/dist/components/dropdown';
import { getFilterDropdownData } from '@/components/TeamAndSchedule/services/teamAndScheduleService';
import CollapsibleFacet from '@/components/TeamAndSchedule/UI/molecules/collapsible-section/CollapsibleSection.vue';
import { useSessionStorage } from '@agyletime/vue-3-common/src/composables/sessionStorage';
import { useCurrentUser } from '@/components/TeamAndSchedule/composables/useCurrentUser';

interface Props {
    activeTable: 'teamSchedule';
    query: {
        [key: string]: unknown;
    };
}

const props = defineProps<Props>();
const resetKey = ref(0);
const isLoading = ref(true);
const sessionStorage = useSessionStorage();

const { getCurrentUser } = useCurrentUser();
const currentUser = getCurrentUser();

const getFacetBindings = (facet: FacetConfig) => {
    const options = dynamicData.value[props.activeTable][facet.id];
    const bindings: Record<string, unknown> = facet.needsData ? { options } : {};

    if ('labelMap' in facet) {
        bindings.labelMap = facet.labelMap;
    }

    return bindings;
};
const facetConfig = computed(() => getFacetConfig(props.query));

const dynamicData = ref<Record<string, Record<string, unknown>>>({ teamSchedule: {} });
const facetsChanges = ref<Record<string, Record<string, FacetItem | unknown>>>({});
const modifiedFacetId = ref<string | null>(null);
const activeFacets = computed(() => {
    const allFacets = facetConfig.value[props.activeTable];
    return allFacets.filter((facet) => {
        if (facet.id === 'leaveTasks') {
            const hasPermission = currentUser?.permissions?.includes('can_view_leave_type');
            const leaveTasksData = dynamicData.value[props.activeTable]?.leaveTasks;
            const hasLeaveData = Array.isArray(leaveTasksData) && leaveTasksData.length > 0;
            return hasPermission && hasLeaveData;
        }
        return true;
    });
});
const hasActiveFacets = computed(() => facetConfig.value[props.activeTable]?.length > 0);

const facetSections = computed(() => getFacetSections(activeFacets.value));
const queryFilters = ref(props.query);
const resetFilters = inject('resetFilters', ref(false));
const clearFilters = inject('clearFilters', () => {});
const pagePreferences = inject<InjectedPagePreferences>('pagePreferences');
const viewSelected = inject('viewSelected', ref(false));
const facetsLoading = inject('facetsLoading', ref(true)) as Ref<boolean>;

const {
    visibleFacetsForSection,
    trackFacetValues,
    getFacetVisibilityState,
    setFacetVisibilityState,
    resetVisibilityFacetValues
} = useFacetVisibility(() => activeFacets.value, () => props.activeTable, () => viewSelected.value);

/**
 * Hydrate facet options from sessionStorage cache immediately.
 * Returns true if cached data was found and applied, false otherwise.
 * This gives the facet components their `options` props instantly on reload,
 * so they can restore selected values from filterSessionStorage without
 * waiting for the slow API calls.
 */
const hydrateDynamicDataFromCache = (): boolean => {
    const cached = sessionStorage.get('usersData');
    const cachedTable = cached?.[props.activeTable] as Record<string, unknown> | undefined;
    if (cachedTable && Object.keys(cachedTable).length > 0) {
        dynamicData.value[props.activeTable] = cachedTable;
        isLoading.value = false;
        facetsLoading.value = false;
        return true;
    }
    return false;
};

const fetchDynamicData = async (isBackgroundRefresh = false) => {
    if (!isBackgroundRefresh) {
        isLoading.value = true;
        facetsLoading.value = true;
    }
    try {
        const response = await getFilterDropdownData();
        dynamicData.value[props.activeTable] = response as unknown as Record<string, unknown>;
        sessionStorage.set('usersData', dynamicData.value);
    } finally {
        isLoading.value = false;
        facetsLoading.value = false;
    }
};

const resetFacetFilters = () => {
    resetKey.value++;
    modifiedFacetId.value = null;
    facetsChanges.value = {};
    resetVisibilityFacetValues();
    clearFilters();
};

const activeFiltersCount = computed(() => {
    const tableFacets = facetsChanges.value[props.activeTable];
    if (!tableFacets) return 1;
    
    const filterCounts = Object.values(tableFacets).filter((facetValue) => {
        if (!facetValue) return false;
        const fv = facetValue as FacetItem;
        if (Array.isArray(fv.value)) return fv.value.length > 0;
        if (fv.boundaries) return fv.boundaries.length > 0;
        return Boolean(fv.value);
    }).length;

    return facetsChanges.value[props.activeTable]?.dateFacet ? filterCounts : filterCounts + 1;
});

watch(activeFiltersCount, (newCount) => {
    // emit defined later; watcher will execute after setup so emit exists
    emit('filtersCountChanged', newCount);
}, { immediate: true });

watch(resetFilters, (newValue) => {
    if (newValue) {
        resetFacetFilters();
    }
});

watch(
    () => props.query,
    (newQuery, oldQuery) => {
        if (!hasActiveFacets.value) return;

        // const hasDateChanged = newQuery.dateRange !== oldQuery.dateRange;
        const hasQueryChanged = JSON.stringify(newQuery) !== JSON.stringify(oldQuery);

        if (viewSelected.value) {
            handleDateOrViewChange();
        }

        if (hasQueryChanged) {
            queryFilters.value = { ...newQuery };
            facetsChanges.value = (queryFilters.value?.facets || {}) as Record<string, Record<string, unknown>>;
            // fetchDynamicData();
        }

        trackQueryFacets(newQuery);
    }
);

const trackQueryFacets = (query: Props['query']) => {
    if (query.facets && query.facets[props.activeTable as keyof typeof query.facets]) {
        Object.entries(query.facets[props.activeTable as keyof typeof query.facets]).forEach(([ facetId, facetValue ]) => {
            trackFacetValues(facetId, facetValue as FacetItem);
        });
    }
};

const initializeFacetsFromPreferences = () => {
    facetsChanges.value = JSON.parse(JSON.stringify(pagePreferences?.selected.value?.filter?.facets ?? {}));
    
    const savedVisibility = pagePreferences?.selected.value?.filter?.facetVisibility;
    if (savedVisibility) {
        setFacetVisibilityState(savedVisibility[props.activeTable]);
    }
    Object.entries(facetsChanges.value[props.activeTable] || {}).forEach(([ facetId, facetValue ]) => {
        trackFacetValues(facetId, facetValue as FacetItem);
    });
};

const handleDateOrViewChange = () => {
    if (viewSelected.value) {
        initializeFacetsFromPreferences();
        viewSelected.value = false;
    }
};

const emit = defineEmits([
    'updateQuery', 'visibilityChanged', 'filtersCountChanged'
]);
const updateFacet = (id: string, activeTable: string, facetValue: FacetItem) => {
    modifiedFacetId.value = id;

    if (!facetsChanges.value[activeTable]) {
        facetsChanges.value[activeTable] = {};
    }

    facetsChanges.value[activeTable][id] = facetValue;
    
    trackFacetValues(id, facetValue);
    
    emit('updateQuery', facetsChanges.value);
};

onMounted(() => {
    // 1. Immediately hydrate facet options from sessionStorage cache.
    //    This gives facet components their options props instantly, so they
    //    can restore selected values (from filterSessionStorage) without
    //    waiting for the slow dependency API calls.
    const hasCachedData = hydrateDynamicDataFromCache();

    // 2. Always fetch fresh data from API.
    //    If we had cache, this runs as a background refresh (no loading spinner).
    //    If no cache, this is the primary fetch (shows loading spinner).
    fetchDynamicData(hasCachedData);
});

defineExpose({ getFacetVisibilityState });

</script>
<style scoped lang="scss">
.facet-panel {
    display: flex;
    flex-direction: column;
    padding: var(--spacing-2, 8px);
    width: 220px;
    max-width: 300px;
}

.facet-section {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-3, 12px);
    padding: 0;
    margin-bottom: 16px;
}

.facet-section:last-child {
    margin-bottom: 16px;
}

.facet-section:not(:last-child) :deep(.facet-body > *:last-child) {
    margin-bottom: 0 !important;
}

.section-title {
    color: var(--color-text-70, #535859);
    font-family: 'Roboto';
    font-size: var(--spacing-4, 16px);
    font-style: normal;
    font-weight: 400;
    line-height: var(--spacing-5, 20px);
    margin: 0;
}

.facet-visibility-dropdown {
    cursor: pointer;
    box-sizing: border-box;
}

.plus-icon {
    font-size: 16px;
    color: var(--color-text-80, #1A2021);
}

.facet-dropdown-item {
    display: flex;
    align-items: center;
    gap: var(--spacing-2, 8px);
}

.facet-name {
    font-size: var(--spacing-4, 16px);
    color: var(--color-text-80, #1A2021);
    user-select: none;
}

.facet-header {
    width: 100%;
    font-weight: 600;
    padding: 6px 15px;
    box-sizing: border-box;
    border-bottom: 1px solid var(--color-border-card, #DCE3E4);
    font-family: Roboto !important;
    font-weight: 400;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.filter-applied-text {
    font-family: Roboto;
    font-weight: 400;
    font-size: 12px;
    line-height: 24px;
    letter-spacing: 0%;
    color: #000000;
}

.clear-all-link {
    font-family: Roboto;
    font-weight: 500;
    font-style: normal;
    font-size: 12.8px;
    line-height: 100%;
    letter-spacing: 0%;
    color: #31708F;
    cursor: pointer;
    background: none;
    border: none;
    padding: 0;
    text-align: left;
    border-radius: 4px;
}

.clear-all-link:hover {
    color: #2a5f7a;
    text-decoration: underline;
}

.clear-all-link:disabled {
    color: #A0AEB2;
    cursor: not-allowed;
    pointer-events: none;
    opacity: 0.6;
}

.clear-all-link:focus-visible {
    outline: 2px solid #101010;
    outline-offset: 2px;
}

.facet-panel-container {
    border-radius: 4px;
    border: 1px solid var(--color-border-card, #DCE3E4);
    background: #FFF;
}
</style>
